/**
 * Pure path-string helpers.
 *
 * Deliberately a leaf module with no imports. `basenameOfPath` is needed by
 * `vscode-icons`, which almost every surface pulls in — routing it through a
 * module that transitively imports `lib/utils` made the web import-coverage
 * sweep four times slower (6.4s to 25s) purely from graph weight.
 */

/**
 * The final segment of a path.
 *
 * A trailing separator is a valid way to write a directory, so it is trimmed
 * before taking the last segment — otherwise `/tmp/` reads as an empty name and
 * anything labelled by it renders blank. Backslashes count as separators so
 * Windows paths do not come back whole.
 *
 * One implementation on purpose. There were three, disagreeing: the copy in
 * `vscode-icons` returned "" for `/tmp/`, `ChatHeader`'s handled that but not
 * Windows, and the markdown one handled both.
 */
export function basenameOfPath(path: string): string {
  const trimmed = path.replace(/[/\\]+$/, "") || path;
  const separatorIndex = Math.max(trimmed.lastIndexOf("/"), trimmed.lastIndexOf("\\"));
  const segment = separatorIndex >= 0 ? trimmed.slice(separatorIndex + 1) : trimmed;
  // The filesystem root is all separator and no segment. Returning "" there
  // reintroduces the blank label this function exists to prevent, so the
  // original stands in for itself.
  return segment || path;
}
